import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Message } from './PepWiseAIChatPopup.types';
import { renderMarkdownHTML } from './PepWiseAIChatPopup.utils';
import SemanticResultCard from './SemanticResultCard';

const PendingDots: React.FC = () => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, py: 0.5 }}>
        {[0, 1, 2].map((i) => (
            <Box
                key={i}
                component="span"
                sx={{
                    width: 7, height: 7, borderRadius: '50%',
                    backgroundColor: '#8CA3BE',
                    animation: 'pepwiseTyping 1.2s ease-in-out infinite',
                    animationDelay: `${i * 0.18}s`,
                }}
            />
        ))}
    </Box>
);

const MessageBubbleContent: React.FC<{ msg: Message; isAssistant: boolean; parsedSemanticResult: any }> = ({ msg, isAssistant, parsedSemanticResult }) => (
    <>
        {isAssistant && parsedSemanticResult && <SemanticResultCard result={parsedSemanticResult} />}
        <Box>
            <div dangerouslySetInnerHTML={{ __html: isAssistant ? renderMarkdownHTML(msg.content) : msg.content.replaceAll('\n', '<br />') }} />
        </Box>
    </>
);

const MessageBubbleBody: React.FC<{ isPending: boolean | string | undefined; msg: Message; isAssistant: boolean }> = ({ isPending, msg, isAssistant }) => {
    const [showDetails, setShowDetails] = React.useState(false);

    if (isPending) return <PendingDots />;

    let parsedSemanticResult = msg.semanticResult;
    if (typeof parsedSemanticResult === 'string') {
        try {
            parsedSemanticResult = JSON.parse(parsedSemanticResult);
        } catch { }
    }

    let metadata = parsedSemanticResult?.metadata;
    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata);
        } catch { }
    }

    let steps = parsedSemanticResult?.executed_steps || metadata?.executed_steps || metadata?.executed_step;

    if (typeof steps === 'string') {
        try {
            steps = JSON.parse(steps);
        } catch { }
    }

    const hasDetails = isAssistant && steps && Array.isArray(steps) && steps.length > 0;

    return (
        <>
            <MessageBubbleContent msg={msg} isAssistant={isAssistant} parsedSemanticResult={parsedSemanticResult} />
            {hasDetails && (
                <Box sx={{ mt: 2, backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                    <Box
                        onClick={() => setShowDetails(!showDetails)}
                        sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'background-color 0.2s', '&:hover': { backgroundColor: '#F1F5F9' } }}
                    >
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                            Additional Details
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#64748B', transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                            ▼
                        </Typography>
                    </Box>
                    {showDetails && (
                        <Box sx={{ p: 1.5, pt: 0, borderTop: '1px solid #E2E8F0', mt: 1 }}>
                            {steps.map((step: any, idx: number) => {
                                const uid = step.id || (typeof step.query_used === 'string' ? step.query_used.substring(0, 50) : null) || JSON.stringify(step);
                                return (
                                    <Box key={uid} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '11px', color: '#475569', mb: idx < steps.length - 1 ? 1 : 0 }}>
                                        {step.query_used && (
                                            <Box>
                                                <Box component="span" sx={{ fontWeight: 600 }}>Query Used:</Box>{' '}
                                                <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1, backgroundColor: '#EDF2F7', borderRadius: '4px', overflowX: 'auto', fontSize: '10px' }}>
                                                    {typeof step.query_used === 'object' ? JSON.stringify(step.query_used, null, 2) : step.query_used}
                                                </Box>
                                            </Box>
                                        )}
                                        {step.row_count !== undefined && (
                                            <Box><Box component="span" sx={{ fontWeight: 600 }}>Row Count:</Box> {step.row_count}</Box>
                                        )}
                                        {step.execution_ms !== undefined && (
                                            <Box><Box component="span" sx={{ fontWeight: 600 }}>Execution Time:</Box> {step.execution_ms}ms</Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

const MessageBubble: React.FC<{ msg: Message; isStreaming?: boolean; onFeedback?: (msgId: number, feedback: 'up' | 'down') => void }> = ({ msg, isStreaming, onFeedback }) => {
    const isAssistant = msg.role === 'assistant';
    // Show animated typing dots when assistant content is empty (pending reply)
    const isPending = isAssistant && !msg.content?.trim();

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 2, justifyContent: isAssistant ? 'flex-start' : 'flex-end' }}>
            <Box
                sx={{
                    maxWidth: 'min(85%, 800px)',
                    px: 2.5,
                    py: 1.5,
                    borderRadius: isAssistant ? '2px 20px 20px 20px' : '20px 20px 2px 20px',
                    backgroundColor: isAssistant ? '#FFFFFF' : '#003771',
                    boxShadow: isAssistant ? '0px 2px 8px rgba(0,0,0,0.05)' : 'none',
                    border: isAssistant ? '1px solid #E2E8F0' : 'none',
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        color: isAssistant ? '#1E293B' : '#FFFFFF',
                        wordBreak: 'break-word',
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '-0.006em',
                        '& h2, & h3, & h4': {
                            color: isAssistant ? '#003771' : '#FFFFFF',
                            fontWeight: 700,
                        },
                        '& strong': {
                            fontWeight: 700,
                            color: isAssistant ? '#0F172A' : '#FFFFFF',
                        }
                    }}
                >
                    <MessageBubbleBody isPending={isPending} msg={msg} isAssistant={isAssistant} />
                </Typography>
                {msg.time && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                            mt: 0.5,
                        }}
                    >
                        {isAssistant && msg.responseTime && (
                            <Tooltip title="Response Time" placement="top">
                                <Box
                                    component="span"
                                    sx={{
                                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                                        fontWeight: 500,
                                        fontSize: '11px',
                                        lineHeight: '16px',
                                        color: '#64748B',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.3,
                                        backgroundColor: '#F1F5F9',
                                        px: 0.8,
                                        py: 0.2,
                                        borderRadius: '4px',
                                        border: '1px solid #E2E8F0',
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 12, color: '#64748B' }} />
                                    {msg.responseTime}
                                </Box>
                            </Tooltip>
                        )}
                        <Typography
                            sx={{
                                fontFamily: '"Plus Jakarta Sans", sans-serif',
                                fontWeight: 500, fontSize: '12px', lineHeight: '16px',
                                letterSpacing: '-0.005em',
                                color: isAssistant ? '#475569' : 'rgba(255,255,255,0.7)',
                            }}
                        >
                            {msg.time}
                        </Typography>
                    </Box>
                )}
            </Box>
            {isAssistant && msg.turnId && !isPending && !isStreaming && onFeedback && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', alignSelf: 'center', ml: 1 }}>
                    <Tooltip title="Helpful" placement="right">
                        <IconButton size="small" onClick={() => onFeedback(msg.id, 'up')} sx={{ p: 0.5, color: msg.feedback === 'up' ? '#166534' : '#94A3B8', '&:hover': { color: '#166534', backgroundColor: 'rgba(22, 101, 52, 0.08)' } }}>
                            {msg.feedback === 'up' ? <ThumbUpIcon sx={{ fontSize: 16 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Not Helpful" placement="right">
                        <IconButton size="small" onClick={() => onFeedback(msg.id, 'down')} sx={{ p: 0.5, color: msg.feedback === 'down' ? '#991B1B' : '#94A3B8', '&:hover': { color: '#991B1B', backgroundColor: 'rgba(153, 27, 27, 0.08)' } }}>
                            {msg.feedback === 'down' ? <ThumbDownIcon sx={{ fontSize: 16 }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export const TypingBubble: React.FC = () => (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 2, justifyContent: 'flex-start' }}>
        <Box sx={{ px: 2, py: 1, borderRadius: '2px 20px 20px 20px', backgroundColor: '#FFFFFF', boxShadow: '0px 2px 8px rgba(0,0,0,0.05)', display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
            {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#8CA3BE', animation: 'pepwiseTyping 1.2s ease-in-out infinite', animationDelay: `${i * 0.18}s` }} />
            ))}
        </Box>
    </Box>
);

export default MessageBubble;
